// frontend/src/pages/RelogioAgua.js
import React, { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import "./RelogioAgua.css";
import BuscarMatricula from "../components/BuscarMatricula";
import { copiarTexto } from "../utils/clipboard";

import ThemeProvider from "../theme/TemaMuiProvider";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

import { FaEye, FaEdit, FaTimes, FaTrash, FaSave, FaPaperclip } from "react-icons/fa";

import muiTheme from "../theme/muiTheme";
import { mascararCpfCnpj } from "../utils/documento";

// =========================
// MÁSCARAS
// =========================
// CPF ou CNPJ (numérico ou alfanumérico, letras A-Z nas 12 primeiras posições).
const maskCpfCnpj = (value) => mascararCpfCnpj(value);

const maskNumeroRelogio = (value) =>
  String(value ?? "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase()
    .slice(0, 15);

const maskMoney = (value) => {
  let v = String(value ?? "").replace(/\D/g, "");
  v = (Number(v) / 100).toFixed(2) + "";
  return v.replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const parseMoneyToFloat = (value) => {
  const txt = String(value ?? "").trim();
  if (!txt) return null;
  const normalizado = txt.replace(/\./g, "").replace(",", ".");
  const n = parseFloat(normalizado);
  return Number.isFinite(n) ? n : null;
};

// =========================
// HELPERS MATRÍCULA
// =========================
const getCodigoMatricula = (mat) => mat?.codigo_matricula ?? mat?.codigoMatricula ?? mat?.codigo ?? "";
const getApelidoMatricula = (mat) => mat?.apelido ?? mat?.apelido_matricula ?? "";

// =========================
// UPPERCASE
// =========================
const toUpperSafe = (value) => String(value ?? "").toUpperCase();

const camposMaiusculos = [
  "apelido",
  "complemento",
  "categoriaConsumo",
  "nomeProprietario",
  "nomeTitular",
  "observacoes",
  "status",
  "debitoAberto",
  "atualizado",
  "matricula",
  "matriculaCodigo",
  "matriculaApelido",
];

// =========================
// PAYLOAD -> BANCO
// =========================
const montarPayloadBanco = (fd) => {
  const matriculaIdNum = Number(fd.matriculaId);

  return {
    codigo: fd.codigo || undefined,

    apelido: fd.apelido ?? null,
    complemento: fd.complemento ?? null,
    numeroRelogio: fd.numeroRelogio ?? null,
    subNumeroRelogio: fd.subNumeroRelogio ?? null,
    categoriaConsumo: fd.categoriaConsumo ?? null,

    nomeProprietario: fd.nomeProprietario ?? null,
    cpfProprietario: fd.cpfProprietario ?? null,

    nomeConsumidor: fd.nomeTitular ?? null,
    cpfConsumidor: fd.cpfTitular ?? null,

    status: fd.status ?? null,
    debitosEmAberto: fd.debitoAberto ?? null,
    valorDebitos: parseMoneyToFloat(fd.valorDebito),
    mesCompetencia: fd.competencia ?? null,
    cadastroAtualizado: fd.atualizado ?? null,

    observacoes: fd.observacoes ?? null,

    // ⚠️ pode vir errado do BuscarMatricula — backend NÃO depende dele
    matriculaId: Number.isFinite(matriculaIdNum) && matriculaIdNum > 0 ? matriculaIdNum : null,

    // texto (fallback)
    matricula: (fd.matricula ?? "").trim() || null,

    // ✅ manda separado para o backend resolver o FK 100%
    matriculaCodigo: (fd.matriculaCodigo ?? "").trim() || null,
    matriculaApelido: (fd.matriculaApelido ?? "").trim() || null,
  };
};

function RelogioAgua() {
  const [locais, setLocais] = useState([]);
  const [registros, setRegistros] = useState([]);

  const [formData, setFormData] = useState({
    codigo: "",
    apelido: "",
    complemento: "",
    numeroRelogio: "",
    subNumeroRelogio: "",
    categoriaConsumo: "",
    nomeProprietario: "",
    cpfProprietario: "",
    nomeTitular: "",
    cpfTitular: "",

    // ✅ SITUAÇÃO
    status: "",
    debitoAberto: "",
    valorDebito: "",
    competencia: "",
    atualizado: "",

    observacoes: "",

    matriculaId: null,
    matricula: "",
    matriculaCodigo: "",
    matriculaApelido: "",
  });

  const [filtroCodigo, setFiltroCodigo] = useState("");
  const [nomeProprietarioMatricula, setNomeProprietarioMatricula] = useState("");
  const [nomeLocatario, setNomeLocatario] = useState("");

  // =========================
  // BUSCA DE IMÓVEL PELO APELIDO
  // =========================
  const [imoveisBuscaLista, setImoveisBuscaLista] = useState([]);
  const [modalSelecionarImovel, setModalSelecionarImovel] = useState(false);
  const [filtroImovel, setFiltroImovel] = useState("");
  const [paginaImovel, setPaginaImovel] = useState(0);
  const [linhasPorPaginaImovel, setLinhasPorPaginaImovel] = useState(10);

  // GED
  const [arquivos, setArquivos] = useState([]);
  const [modalGED, setModalGED] = useState(false);
  const [codigoSelecionadoGED, setCodigoSelecionadoGED] = useState(null);
  const [gedPendentes, setGedPendentes] = useState([]);

  // =========================
  // ✅ minimizar/expandir grid (persistido)
  // =========================
  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("relogioAguaGridMinimizado");
    return saved ? saved === "1" : false;
  });

  useEffect(() => {
    localStorage.setItem("relogioAguaGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  const normalizarRegistro = (obj) => {
    const lower = Object.fromEntries(Object.entries(obj || {}).map(([chave, valor]) => [String(chave).toLowerCase(), valor]));

    return {
      ...obj,
      ...lower,

      codigo: lower.codigo ?? obj.codigo ?? "",

      apelido: lower.apelido ?? obj.apelido ?? "",

      complemento: lower.complemento ?? obj.complemento ?? "",

      numeroRelogio: lower.numerorelogio ?? obj.numeroRelogio ?? "",

      subNumeroRelogio: lower.subnumerorelogio ?? obj.subNumeroRelogio ?? "",

      categoriaConsumo: lower.categoriaconsumo ?? obj.categoriaConsumo ?? "",

      nomeProprietario: lower.nomeproprietario ?? obj.nomeProprietario ?? "",

      cpfProprietario: lower.cpfproprietario ?? obj.cpfProprietario ?? "",

      nomeConsumidor: lower.nomeconsumidor ?? obj.nomeConsumidor ?? "",

      cpfConsumidor: lower.cpfconsumidor ?? obj.cpfConsumidor ?? "",

      status: String(lower.status ?? obj.status ?? "")
        .trim()
        .toUpperCase(),

      debitosEmAberto: String(lower.debitosemaberto ?? obj.debitosEmAberto ?? "")
        .trim()
        .toUpperCase(),

      valorDebitos: lower.valordebitos ?? obj.valorDebitos ?? "",

      mesCompetencia: lower.mescompetencia ?? obj.mesCompetencia ?? "",

      cadastroAtualizado: String(lower.cadastroatualizado ?? obj.cadastroAtualizado ?? "")
        .trim()
        .toUpperCase(),

      observacoes: lower.observacoes ?? obj.observacoes ?? "",

      matricula: lower.matricula ?? obj.matricula ?? "",

      matriculaId: lower.matriculaid ?? obj.matriculaId ?? null,

      qtdArquivos: lower.qtdarquivos ?? obj.qtdArquivos ?? 0,
    };
  };

  const definirRegistros = (data) => {
    const lista = Array.isArray(data)
      ? data
      : Array.isArray(data?.recordset)
        ? data.recordset
        : Array.isArray(data?.registros)
          ? data.registros
          : data && typeof data === "object"
            ? [data]
            : [];

    setRegistros(lista.map(normalizarRegistro));
  };

  // ✅ controla abertura do bloco/modal de situação
  const [mostrarSituacao, setMostrarSituacao] = useState(false);

  // =========================
  // PERMISSÕES
  // =========================
  const token = localStorage.getItem("token");
  let tipoUsuario = "";

  if (token) {
    const decoded = jwtDecode(token);
    tipoUsuario = decoded.tipo
      ?.toString()
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  const [modoVisualizacao, setModoVisualizacao] = useState(false);
  const [idVisualizando, setIdVisualizando] = useState(null);
  const [editandoCodigo, setEditandoCodigo] = useState(null);

  const formTravado = modoVisualizacao && !editandoCodigo;
  const podeMostrarExcluirNoForm = modoVisualizacao && !editandoCodigo && Boolean(idVisualizando);

  const [mostrarPendencias, setMostrarPendencias] = useState(false);

  const erroCampoMUI = (campo) => {
    if (!mostrarPendencias) return false;
    if (!camposOpcionais.includes(campo)) return false;

    return estaVazio(formData[campo]);
  };

  const muiCampoSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      display: "flex",
      alignItems: "center",
      backgroundColor: "transparent",

      "& fieldset": {
        borderColor: "var(--ig-b-555555)",
      },

      "&:hover fieldset": {
        borderColor: "var(--ig-b-777777)",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#2ea8ff",
        borderWidth: 2,
      },

      "&.Mui-error fieldset": {
        borderColor: "#ef4444 !important",
        borderWidth: 2,
      },

      "&.Mui-error:hover fieldset": {
        borderColor: "#ef4444 !important",
      },

      "&.Mui-error.Mui-focused fieldset": {
        borderColor: "#ef4444 !important",
        borderWidth: 2,
      },

      "&.Mui-disabled fieldset": {
        borderColor: "var(--ig-b-555555)",
      },
    },

    "& .MuiOutlinedInput-input": {
      padding: "16.5px 14px",
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "var(--ig-t-ffffff)",
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiInputLabel-root": {
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#2ea8ff",
    },

    "& .MuiInputLabel-root.Mui-error": {
      color: "#ef4444 !important",
    },

    "& .MuiInputLabel-root.Mui-disabled": {
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiSvgIcon-root": {
      color: "var(--ig-t-ffffff)",
    },

    '& input[type="month"]::-webkit-calendar-picker-indicator': {
      filter: "var(--ig-x-11o99ka)",
      opacity: 1,
      cursor: "pointer",
    },
  };

  const menuPropsDark = {
    MenuProps: {
      PaperProps: {
        sx: {
          backgroundColor: "var(--ig-f-111827)",
          color: "var(--ig-t-ffffff)",

          "& .MuiMenuItem-root": {
            color: "var(--ig-t-ffffff)",
          },

          "& .MuiMenuItem-root:hover": {
            backgroundColor: "var(--ig-f-1f2937)",
          },

          "& .MuiMenuItem-root.Mui-selected": {
            backgroundColor: "var(--ig-f-1f2937) !important",
          },
        },
      },
    },
  };

  const resetForm = () => {
    setFormData({
      codigo: "",
      apelido: "",
      complemento: "",
      numeroRelogio: "",
      subNumeroRelogio: "",
      categoriaConsumo: "",
      nomeProprietario: "",
      cpfProprietario: "",
      nomeTitular: "",
      cpfTitular: "",

      status: "",
      debitoAberto: "",
      valorDebito: "",
      competencia: "",
      atualizado: "",

      observacoes: "",

      matriculaId: null,
      matricula: "",
      matriculaCodigo: "",
      matriculaApelido: "",
    });

    setMostrarSituacao(false);
    setModoVisualizacao(false);
    setIdVisualizando(null);
    setEditandoCodigo(null);

    setNomeProprietarioMatricula("");
    setNomeLocatario("");
    setMostrarPendencias(false);

    setGedPendentes([]);
    setArquivos([]);
    setModalGED(false);
    setCodigoSelecionadoGED(null);

    setModalSelecionarImovel(false);
    setFiltroImovel("");
    setPaginaImovel(0);
  };

  const carregarRegistros = async () => {
    const tokenLocal = localStorage.getItem("token");
    try {
      const res = await fetch("/api/relogio-agua", {
        headers: { Authorization: `Bearer ${tokenLocal}` },
      });
      const data = await res.json();
      definirRegistros(data);
    } catch (err) {
      console.error("Erro ao buscar registros:", err);
    }
  };

  // =========================
  // PAGINAÇÃO
  // =========================
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [registrosPorPagina, setRegistrosPorPagina] = useState(5);

  const registrosFiltrados = Array.isArray(registros)
    ? registros.filter((item) => {
        if (!filtroCodigo.trim()) return true;

        const termos = filtroCodigo
          .toLowerCase()
          .split(/[,;]+/)
          .map((t) => t.trim())
          .filter((t) => t.length > 0);

        const registroTexto = Object.values(item)
          .map((v) => String(v ?? "").toLowerCase())
          .join(" ");

        return termos.every((term) => registroTexto.includes(term));
      })
    : [];

  const totalPaginas = Math.max(1, Math.ceil(registrosFiltrados.length / registrosPorPagina));
  const indexInicial = (paginaAtual - 1) * registrosPorPagina;
  const indexFinal = indexInicial + registrosPorPagina;
  const registrosPaginados = registrosFiltrados.slice(indexInicial, indexFinal);

  // ✅ evita página vazia ao filtrar
  useEffect(() => {
    if (paginaAtual > totalPaginas) setPaginaAtual(1);
  }, [paginaAtual, totalPaginas]);

  const proprietarioIgual = (formData.nomeProprietario ?? "").trim().toUpperCase() === (nomeProprietarioMatricula ?? "").trim().toUpperCase() ? "Sim" : "Não";

  const titularIgual = (formData.nomeTitular ?? "").trim().toUpperCase() === (nomeLocatario ?? "").trim().toUpperCase() ? "Sim" : "Não";

  // =========================
  // FILTRO DO MODAL DE IMÓVEIS
  // =========================
  const imoveisFiltradosBusca = imoveisBuscaLista.filter((imovel) => {
    const termo = filtroImovel
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    if (!termo) return true;

    const textoImovel = [
      imovel.codigo,
      imovel.codigoMatricula,
      imovel.codigo_matricula,
      imovel.apelido,
      imovel.endereco,
      imovel.bairro,
      imovel.cidade,
      imovel.estado,
      imovel.uf,
      imovel.status,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    return textoImovel.includes(termo);
  });

  const imoveisPaginadosBusca = imoveisFiltradosBusca.slice(paginaImovel * linhasPorPaginaImovel, paginaImovel * linhasPorPaginaImovel + linhasPorPaginaImovel);

  const selecionarImovelApelido = (imovel) => {
    setFormData((prev) => ({
      ...prev,
      apelido: String(imovel.apelido || "")
        .trim()
        .toUpperCase(),
    }));

    setModalSelecionarImovel(false);
    setFiltroImovel("");
    setPaginaImovel(0);
  };

  // GED

  // GED
  const baixarArquivoGED = async (codigo, arquivoId, nome) => {
    try {
      const resp = await fetch(`/api/relogio-agua/${codigo}/ged/${arquivoId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!resp.ok) throw new Error("Falha no download");

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = nome || "arquivo";
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Erro ao baixar arquivo.");
    }
  };
  const visualizarArquivoGED = async (codigo, arquivoId, nomeArquivo = "") => {
    if (!codigo || !arquivoId) {
      alert("Arquivo inválido para visualização.");
      return;
    }

    const nome = String(nomeArquivo || arquivoId || "").toLowerCase();

    const extensoesVisualizaveis = [".pdf", ".png", ".jpg", ".jpeg", ".gif", ".webp", ".bmp", ".svg"];

    const podeVisualizarArquivo = extensoesVisualizaveis.some((extensao) => nome.endsWith(extensao));

    if (!podeVisualizarArquivo) {
      const confirmar = window.confirm(
        `Visualização indisponível.\n\n` +
          `O arquivo "${nomeArquivo}" não possui visualização integrada no ImobGest.\n\n` +
          `Para visualizar esse tipo de arquivo, é necessário baixar e abrir em um aplicativo compatível.\n\n` +
          `Deseja baixar agora?`,
      );

      if (confirmar) {
        baixarArquivoGED(codigo, arquivoId, nomeArquivo);
      }

      return;
    }

    try {
      const resp = await fetch(`/api/relogio-agua/${encodeURIComponent(codigo)}/ged/${encodeURIComponent(arquivoId)}/visualizar`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!resp.ok) {
        const erro = await resp.json().catch(() => ({}));

        throw new Error(erro?.erro || erro?.mensagem || "Erro ao visualizar arquivo GED.");
      }

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);

      window.open(url, "_blank", "noopener,noreferrer");

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 60000);
    } catch (err) {
      console.error("Erro ao visualizar arquivo GED:", err);

      alert(err?.message || "Erro ao visualizar arquivo GED.");
    }
  };

  const excluirArquivoGED = async (codigo, arquivoId) => {
    if (!window.confirm("Deseja excluir este arquivo?")) return;

    try {
      const resp = await fetch(`/api/relogio-agua/${codigo}/ged/${arquivoId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!resp.ok) {
        throw new Error("Erro ao excluir GED");
      }

      setArquivos((old) => old.filter((arquivo) => String(arquivo.id) !== String(arquivoId)));

      await carregarRegistros();

      alert("Arquivo excluído com sucesso!");
    } catch (err) {
      console.error(err);
      alert("Erro ao excluir arquivo.");
    }
  };

  const enviarGED = async (codigo, files) => {
    const formDataUpload = new FormData();
    files.forEach((file) => formDataUpload.append("files", file));

    try {
      const resp = await fetch(`/api/relogio-agua/${codigo}/ged`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formDataUpload,
      });

      return resp.ok;
    } catch (err) {
      console.error("Erro ao enviar GED:", err);
      return false;
    }
  };

  const abrirGED = async (codigo) => {
    if (!codigo) return;

    setCodigoSelecionadoGED(codigo);
    setModalGED(true);

    try {
      const resp = await fetch(`/api/relogio-agua/${codigo}/ged`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const dados = await resp.json();
      setArquivos(Array.isArray(dados) ? dados : []);
    } catch (err) {
      console.error("Erro ao carregar arquivos GED:", err);
      setArquivos([]);
    }
  };

  useEffect(() => {
    const carregarArquivosDoForm = async () => {
      const codigoAtual = editandoCodigo || idVisualizando;
      if (!codigoAtual) {
        setArquivos([]);
        return;
      }

      try {
        const resp = await fetch(`/api/relogio-agua/${codigoAtual}/ged`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const dados = await resp.json();
        setArquivos(Array.isArray(dados) ? dados : []);
      } catch {
        setArquivos([]);
      }
    };

    carregarArquivosDoForm();
  }, [editandoCodigo, idVisualizando]);

  // =========================
  // BUSCA DE DADOS
  // =========================
  useEffect(() => {
    const tokenLocal = localStorage.getItem("token");

    fetch("/api/complementos", {
      headers: { Authorization: `Bearer ${tokenLocal}` },
    })
      .then((res) => res.json())
      .then((data) => {
        const lista = Array.isArray(data) ? data : Array.isArray(data.complementos) ? data.complementos : Array.isArray(data.recordset) ? data.recordset : [];
        setLocais(lista);
      })
      .catch((err) => console.error("Erro ao buscar complementos:", err));

    fetch("/api/relogio-agua", {
      headers: {
        Authorization: `Bearer ${tokenLocal}`,
      },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => []);

        if (!res.ok) {
          console.error("Erro ao buscar registros:", data);
          return;
        }

        definirRegistros(data);
      })
      .catch((err) => console.error("Erro ao buscar registros:", err));

    fetch("/api/imoveis", {
      headers: {
        Authorization: `Bearer ${tokenLocal}`,
      },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => []);

        if (!res.ok) {
          console.error("Erro ao buscar imóveis:", data);

          setImoveisBuscaLista([]);
          return;
        }

        const lista = Array.isArray(data) ? data : Array.isArray(data?.imoveis) ? data.imoveis : Array.isArray(data?.recordset) ? data.recordset : [];

        setImoveisBuscaLista(lista);
      })
      .catch((err) => {
        console.error("Erro ao buscar imóveis:", err);

        setImoveisBuscaLista([]);
      });
  }, []);

  // =========================
  // CAMPOS OPCIONAIS
  // =========================
  const camposOpcionais = [
    "complemento",
    "numeroRelogio",
    "subNumeroRelogio",
    "categoriaConsumo",
    "nomeProprietario",
    "cpfProprietario",
    "nomeTitular",
    "cpfTitular",
    "status",
    "debitoAberto",
    "valorDebito",
    "competencia",
    "atualizado",
    "observacoes",
    "matricula",
  ];

  const estaVazio = (v) => !String(v ?? "").trim();

  const classeCampoOpcional = (campo) => {
    if (!mostrarPendencias) return "";
    return camposOpcionais.includes(campo) && estaVazio(formData[campo]) ? "campo-pendente" : "";
  };

  // =========================
  // HANDLE CHANGE
  // =========================
  const handleChange = (e) => {
    if (formTravado) return;

    const { name, value } = e.target;
    let maskedValue = value;

    if (name === "cpfProprietario" || name === "cpfTitular") maskedValue = maskCpfCnpj(value);
    if (name === "numeroRelogio" || name === "subNumeroRelogio") {
      maskedValue = maskNumeroRelogio(value);
    }
    if (name === "valorDebito") maskedValue = maskMoney(value);

    if (camposMaiusculos.includes(name)) {
      maskedValue = toUpperSafe(maskedValue);
    }

    // ✅ igual ao Energia: se mudar para "NÃO", limpa valor
    if (name === "debitoAberto" && maskedValue !== "SIM") {
      setFormData((prev) => ({ ...prev, debitoAberto: maskedValue, valorDebito: "" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: maskedValue }));
  };

  // =========================
  // VISUALIZAR
  // =========================
  const handleVisualizar = (item) => {
    if (!podeVisualizar) {
      alert("Você não tem permissão para visualizar.");
      return;
    }

    // tenta quebrar "MT003 - RAGM46" para preencher codigo/apelido (ajuda no PUT)
    const matStr = item.matricula ?? "";
    const parts = String(matStr)
      .split("-")
      .map((p) => p.trim())
      .filter(Boolean);
    const matCodigo = parts[0] || "";
    const matApelido = parts.slice(1).join(" - ").trim() || "";

    setFormData({
      codigo: item.codigo || "",
      apelido: item.apelido || "",
      complemento: item.complemento || "",
      numeroRelogio: item.numeroRelogio || "",
      subNumeroRelogio: item.subNumeroRelogio || "",
      categoriaConsumo: item.categoriaConsumo || "",
      nomeProprietario: item.nomeProprietario || "",
      cpfProprietario: item.cpfProprietario || "",

      nomeTitular: item.nomeConsumidor || "",
      cpfTitular: item.cpfConsumidor || "",

      status: item.status || "",
      debitoAberto: item.debitosEmAberto || "",
      valorDebito:
        item.valorDebitos === null || item.valorDebitos === undefined || item.valorDebitos === ""
          ? ""
          : maskMoney(String(item.valorDebitos).replace(/[^\d.,-]/g, "")),
      competencia: item.mesCompetencia || "",
      atualizado: item.cadastroAtualizado || "",

      observacoes: item.observacoes || "",

      matriculaId: item.matriculaId ?? null,
      matricula: item.matricula ?? "",
      matriculaCodigo: matCodigo,
      matriculaApelido: matApelido,
    });

    setIdVisualizando(item.codigo);
    setEditandoCodigo(null);
    setModoVisualizacao(true);

    setMostrarPendencias(true);
    setMostrarSituacao(false); // ao abrir um registro, começa fechado
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // =========================
  // SALVAR
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMostrarPendencias(true);

    const tokenLocal = localStorage.getItem("token");

    if (!editandoCodigo && !podeCadastrar) return alert("Você não tem permissão para cadastrar.");
    if (editandoCodigo && !podeEditar) return alert("Você não tem permissão para editar.");

    const payload = montarPayloadBanco({
      ...formData,
      codigo: editandoCodigo ? editandoCodigo : formData.codigo,
    });

    try {
      const res = await fetch(editandoCodigo ? `/api/relogio-agua/${editandoCodigo}` : "/api/relogio-agua", {
        method: editandoCodigo ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenLocal}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        const codigoSalvo = editandoCodigo || data?.codigo || formData.codigo;

        alert(editandoCodigo ? "Registro atualizado com sucesso!" : "Cadastro realizado com sucesso!");

        if (codigoSalvo && gedPendentes.length > 0) {
          const okGed = await enviarGED(codigoSalvo, gedPendentes);

          if (okGed) {
            setGedPendentes([]);
            await carregarRegistros();
            await abrirGED(codigoSalvo);
            alert("Anexos GED pendentes enviados!");
          } else {
            await carregarRegistros();
            alert("Registro salvo, mas falhou ao enviar os anexos GED pendentes.");
          }
        } else {
          await carregarRegistros();
        }

        if (!editandoCodigo) {
          setPaginaAtual(1);
        }

        resetForm();
      } else {
        alert(data?.mensagem || data?.erro || "Erro ao salvar.");
        console.log("Resposta erro:", res.status, data);
      }
    } catch (err) {
      console.error("Erro ao salvar:", err);
      alert("Erro ao salvar.");
    }
  };

  // =========================
  // CSV (BOM + Excel compatível)
  // =========================
  const exportarCSV = () => {
    const BOM = "\uFEFF";

    const header = [
      "Código",
      "Apelido",
      "Complemento",
      "N° Relógio",
      "Sub N° Relógio",
      "Categoria",
      "Nome Proprietário",
      "CPF Proprietário",
      "Nome Titular",
      "CPF Titular",
      "Status",
      "Débito Aberto",
      "Valor Débito",
      "Competência",
      "Atualizado",
      "Observações",
      "Matrícula",
    ];

    const csvEscape = (value) => {
      const s = String(value ?? "");
      if (/[;"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const excelText = (value) => `="${String(value ?? "")}"`;

    const linhas = (registrosFiltrados || []).map((r) =>
      [
        r.codigo ?? "",
        r.apelido ?? "",
        r.complemento ?? "",
        excelText(r.numeroRelogio ?? ""),
        excelText(r.subNumeroRelogio ?? ""),
        r.categoriaConsumo ?? "",
        r.nomeProprietario ?? "",
        excelText(r.cpfProprietario ?? ""),
        r.nomeConsumidor ?? "",
        excelText(r.cpfConsumidor ?? ""),
        r.status ?? "",
        r.debitosEmAberto ?? "",
        excelText(
          r.valorDebitos === null || r.valorDebitos === undefined || r.valorDebitos === "" ? "" : maskMoney(String(r.valorDebitos).replace(/[^\d.,-]/g, "")),
        ),
        excelText(r.mesCompetencia ?? ""),
        r.cadastroAtualizado ?? "",
        r.observacoes ?? "",
        excelText(r.matricula ?? ""),
      ].map(csvEscape),
    );

    const csv = [header.map(csvEscape), ...linhas].map((l) => l.join(";")).join("\n");
    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "relogios_agua.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  // =========================
  // EXCLUIR
  // =========================
  const handleDelete = async (codigo) => {
    if (!codigo) return alert("Erro: código inválido para exclusão.");
    if (!podeExcluir) return alert("Você não tem permissão para excluir.");

    const confirmar = window.confirm("Tem certeza que deseja excluir este registro?");
    if (!confirmar) return;

    const tokenLocal = localStorage.getItem("token");

    try {
      const res = await fetch(`/api/relogio-agua/${codigo}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${tokenLocal}` },
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        alert(data.mensagem || "Registro excluído com sucesso!");
        setRegistros((prev) => prev.filter((r) => r.codigo !== codigo));
        if (!editandoCodigo) {
          setPaginaAtual(1);
        }

        resetForm();
      } else {
        alert(data.mensagem || "Erro ao excluir registro.");
      }
    } catch (err) {
      console.error("Erro ao excluir:", err);
      alert("Erro ao excluir registro.");
    }
  };

  const temGedNoForm =
    ((editandoCodigo || idVisualizando) && arquivos.length > 0) ||
    gedPendentes.length > 0 ||
    (registros.find((r) => r.codigo === (editandoCodigo || idVisualizando))?.qtdArquivos || 0) > 0;

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="relogioagua-container">
        <h2>Relógios de Água</h2>

        {/* BLOCO DE BUSCA */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              height: 52,
              width: "100%",
              border: "1px solid var(--ig-b-555555)",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              backgroundColor: "transparent",
              boxSizing: "border-box",
              px: 2,
            }}
          >
            <input
              type="text"
              placeholder="Filtrar Relógio de Água"
              value={filtroCodigo}
              onChange={(e) => {
                setFiltroCodigo(e.target.value);
                setPaginaAtual(1);
              }}
              style={{
                flex: 1,
                height: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                color: "var(--ig-t-ffffff)",
                fontSize: "1rem",
              }}
            />

            <SearchIcon
              sx={{
                color: "var(--ig-t-9ca3af)",
                fontSize: 20,
                flexShrink: 0,
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={() => {
                setFiltroCodigo("");
                setPaginaAtual(1);
              }}
              sx={{
                height: 52,
                color: "#2196f3",
                borderColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                whiteSpace: "nowrap",

                "&:hover": {
                  borderColor: "#42a5f5",
                  backgroundColor: "rgba(33, 150, 243, 0.08)",
                },
              }}
            >
              Limpar Filtro
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              startIcon={<i className="fas fa-copy" />}
              onClick={async () => {
                const texto = registrosFiltrados.map((registro) => registro.codigo).join("\n");

                if (!texto.trim()) {
                  alert("Nada para copiar.");
                  return;
                }

                const ok = await copiarTexto(texto);

                alert(ok ? "Códigos copiados!" : "Não foi possível copiar.");
              }}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",

                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              Copiar
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={exportarCSV}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",

                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              CSV
            </Button>
          </Box>
        </Box>

        {/* ✅ BOTÃO MINIMIZAR/EXPANDIR (sempre visível) */}
        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button
              type="button"
              className="btn-grid-toggle small danger"
              onClick={() => setGridMinimizado(true)}
              title="Minimizar o Grid"
              aria-label="Minimizar o Grid"
              disabled={!podeVisualizar}
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              className="btn-grid-toggle small outline"
              onClick={() => setGridMinimizado(false)}
              title="Expandir o Grid"
              aria-label="Expandir o Grid"
              disabled={!podeVisualizar}
            >
              ↗ Expandir
            </button>
          )}
        </div>

        {/* LISTAGEM */}
        {!gridMinimizado && (
          <div className="bloco-listagem">
            {registrosFiltrados.length === 0 ? (
              <p>Nenhum registro encontrado.</p>
            ) : (
              <>
                <table className="tabela-registro tabela-relogio-agua">
                  <colgroup>
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "24%" }} />
                    <col style={{ width: "24%" }} />
                  </colgroup>

                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Apelido</th>
                      <th>Nº Relógio</th>
                      <th>Status</th>
                      <th>Matrícula</th>
                      <th className="col-acoes">Ações</th>
                    </tr>
                  </thead>

                  <tbody>
                    {registrosPaginados.map((item) => (
                      <tr key={item.codigo}>
                        <td>{item.codigo}</td>
                        <td>{item.apelido}</td>
                        <td>{item.numeroRelogio}</td>
                        <td>{item.status}</td>
                        <td>{item.matricula}</td>

                        <td className="col-acoes">
                          <button
                            className={`btn-ged-lista ${Number(item.qtdArquivos || 0) > 0 ? "btn-ged-ok" : ""}`}
                            type="button"
                            onClick={() => item.codigo && abrirGED(item.codigo)}
                          >
                            GED
                            {Number(item.qtdArquivos || 0) > 0 && <span className="ged-badge">{item.qtdArquivos}</span>}
                          </button>

                          {podeVisualizar && (
                            <button className="btn-editar" type="button" onClick={() => handleVisualizar(item)}>
                              <FaEye />
                              Visualizar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <TablePagination
                  component="div"
                  count={registrosFiltrados.length}
                  page={paginaAtual - 1}
                  onPageChange={(_, novaPagina) => {
                    setPaginaAtual(novaPagina + 1);
                  }}
                  rowsPerPage={registrosPorPagina}
                  rowsPerPageOptions={[5, 10, 25]}
                  onRowsPerPageChange={(e) => {
                    setRegistrosPorPagina(parseInt(e.target.value, 10));

                    setPaginaAtual(1);
                  }}
                  labelRowsPerPage="Relógios por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                  sx={{
                    width: "100%",
                    color: "var(--ig-t-ffffff)",
                    borderTop: "1px solid var(--ig-b-444444)",
                    mt: 1,

                    "& .MuiTablePagination-toolbar": {
                      minHeight: 58,
                      paddingLeft: 0,
                      paddingRight: 0,
                    },

                    "& .MuiTablePagination-spacer": {
                      flex: "1 1 auto",
                    },

                    "& .MuiTablePagination-selectLabel": {
                      color: "var(--ig-t-ffffff)",
                      fontSize: "0.85rem",
                      margin: 0,
                    },

                    "& .MuiTablePagination-displayedRows": {
                      color: "var(--ig-t-ffffff)",
                      fontSize: "0.85rem",
                      margin: 0,
                    },

                    "& .MuiTablePagination-select": {
                      color: "var(--ig-t-ffffff)",
                    },

                    "& .MuiTablePagination-selectIcon": {
                      color: "var(--ig-t-ffffff)",
                    },

                    "& .MuiTablePagination-actions button": {
                      color: "var(--ig-t-ffffff)",
                    },
                  }}
                />

                {!podeCadastrar && (
                  <div className="aviso-permissao-container">
                    <p className="aviso-permissao">🔒 Você possui acesso apenas de visualização.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* MODAL SELECIONAR IMÓVEL */}
        <Dialog
          open={modalSelecionarImovel}
          onClose={() => {
            setModalSelecionarImovel(false);
            setFiltroImovel("");
            setPaginaImovel(0);
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: "var(--ig-f-2c2c2c)",
              color: "var(--ig-t-ffffff)",
              backgroundImage: "none",
              maxHeight: "88vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              color: "var(--ig-t-ffffff)",
              fontWeight: 700,
              px: 3,
              py: 2,
            }}
          >
            Selecionar Imóvel
            <IconButton
              type="button"
              onClick={() => {
                setModalSelecionarImovel(false);
                setFiltroImovel("");
                setPaginaImovel(0);
              }}
            >
              <CloseIcon sx={{ color: "var(--ig-t-aaaaaa)" }} />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{
              px: 3,
              pb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <TextField
                value={filtroImovel}
                onChange={(e) => {
                  setFiltroImovel(e.target.value);
                  setPaginaImovel(0);
                }}
                placeholder="Código, apelido, endereço, bairro ou cidade..."
                fullWidth
                size="small"
                autoFocus
                sx={muiCampoSx}
              />

              <Button
                type="button"
                variant="contained"
                sx={{
                  minWidth: 120,
                  height: 56,
                  textTransform: "none",
                  fontWeight: 700,
                  backgroundColor: "#2196f3",

                  "&:hover": {
                    backgroundColor: "#1976d2",
                  },
                }}
              >
                Pesquisar
              </Button>
            </Box>

            <Table
              size="small"
              sx={{
                tableLayout: "fixed",
                width: "100%",

                "& .MuiTableCell-root": {
                  color: "var(--ig-t-ffffff)",
                  borderBottom: "1px solid var(--ig-b-444444)",
                  py: 1.2,
                },

                "& .MuiTableHead-root .MuiTableCell-root": {
                  backgroundColor: "var(--ig-f-1e1e1e)",
                  fontWeight: 700,
                },

                "& .MuiTableBody-root .MuiTableRow-root": {
                  cursor: "pointer",
                },

                "& .MuiTableBody-root .MuiTableRow-root:hover": {
                  backgroundColor: "var(--ig-f-255-255-255-a008)",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "9%" }}>Código</TableCell>

                  <TableCell sx={{ width: "12%" }}>Apelido</TableCell>

                  <TableCell sx={{ width: "26%" }}>Endereço</TableCell>

                  <TableCell sx={{ width: "26%" }}>Bairro</TableCell>

                  <TableCell sx={{ width: "16%" }}>Cidade/UF</TableCell>

                  <TableCell sx={{ width: "11%" }}>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {imoveisPaginadosBusca.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      sx={{
                        py: 3,
                        color: "var(--ig-t-cccccc) !important",
                      }}
                    >
                      Nenhum imóvel encontrado.
                    </TableCell>
                  </TableRow>
                ) : (
                  imoveisPaginadosBusca.map((imovel) => (
                    <TableRow
                      key={imovel.id || imovel.codigo || imovel.codigoMatricula || imovel.codigo_matricula || imovel.apelido}
                      hover
                      onClick={() => selecionarImovelApelido(imovel)}
                    >
                      <TableCell>{imovel.codigo || imovel.codigoMatricula || imovel.codigo_matricula || ""}</TableCell>

                      <TableCell
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        {imovel.apelido || ""}
                      </TableCell>

                      <TableCell>{imovel.endereco || ""}</TableCell>

                      <TableCell>{imovel.bairro || ""}</TableCell>

                      <TableCell>{[imovel.cidade, imovel.estado || imovel.uf].filter(Boolean).join("/")}</TableCell>

                      <TableCell>{imovel.status || ""}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={imoveisFiltradosBusca.length}
              page={paginaImovel}
              onPageChange={(_, novaPagina) => {
                setPaginaImovel(novaPagina);
              }}
              rowsPerPage={linhasPorPaginaImovel}
              onRowsPerPageChange={(e) => {
                setLinhasPorPaginaImovel(parseInt(e.target.value, 10));

                setPaginaImovel(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              sx={{
                color: "var(--ig-t-ffffff)",
                borderTop: "1px solid var(--ig-b-444444)",

                "& .MuiTablePagination-selectLabel": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiTablePagination-displayedRows": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiTablePagination-select": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiTablePagination-selectIcon": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiTablePagination-actions button": {
                  color: "var(--ig-t-ffffff)",
                },
              }}
            />
          </DialogContent>
        </Dialog>

        {/* MODAL GED */}
        {modalGED && (
          <div className="modal-ged-overlay">
            <div className="modal-ged">
              <h3>Arquivos GED - Relógio de Água {codigoSelecionadoGED}</h3>

              {arquivos.length === 0 ? (
                <p>Nenhum arquivo cadastrado para este registro.</p>
              ) : (
                <ul className="ged-lista-arquivos">
                  {arquivos.map((arquivo) => {
                    const arquivoId = arquivo.id || arquivo.nome;

                    return (
                      <li key={arquivoId} className="ged-item-arquivo">
                        <span className="ged-nome-arquivo">{arquivo.nome}</span>

                        <div className="ged-acoes-arquivo">
                          <button
                            type="button"
                            className="btn-visualizar-ged"
                            onClick={() => visualizarArquivoGED(codigoSelecionadoGED, arquivoId, arquivo.nome)}
                          >
                            <FaEye />
                            Visualizar
                          </button>

                          <button type="button" className="btn-baixar-ged" onClick={() => baixarArquivoGED(codigoSelecionadoGED, arquivoId, arquivo.nome)}>
                            <i className="fas fa-download" />
                            Baixar
                          </button>

                          {podeExcluir && (
                            <button type="button" className="btn-excluir-ged" onClick={() => excluirArquivoGED(codigoSelecionadoGED, arquivoId)}>
                              <FaTrash />
                              Excluir
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              <button type="button" className="btn-fechar-ged" onClick={() => setModalGED(false)}>
                Fechar
              </button>
            </div>
          </div>
        )}

        {/* FORMULÁRIO */}
        {(podeCadastrar || podeVisualizar) && (
          <div className="bloco-formulario relogio-agua-formulario">
            <form className="relogioagua-form form-grid" onSubmit={handleSubmit}>
              {/* ABAS */}
              <div className="abas-formulario relogio-abas-formulario">
                <button type="button" className={`aba ${!mostrarSituacao ? "ativa" : ""}`} onClick={() => setMostrarSituacao(false)}>
                  Dados do Relógio
                </button>

                <button type="button" className={`aba ${mostrarSituacao ? "ativa" : ""}`} onClick={() => setMostrarSituacao(true)}>
                  Situação do Relógio
                </button>
              </div>

              {/* ABA DADOS */}
              {!mostrarSituacao && (
                <div className="conteudo-aba-formulario">
                  <div className="form-row relogio-row-1">
                    <TextField
                      label="Apelido*"
                      name="apelido"
                      value={formData.apelido || ""}
                      onClick={() => {
                        if (formTravado) return;

                        setFiltroImovel("");
                        setPaginaImovel(0);
                        setModalSelecionarImovel(true);
                      }}
                      disabled={formTravado}
                      fullWidth
                      size="small"
                      sx={{
                        ...muiCampoSx,

                        "& .MuiOutlinedInput-root": {
                          ...muiCampoSx["& .MuiOutlinedInput-root"],

                          cursor: formTravado ? "default" : "pointer",
                        },

                        "& .MuiInputBase-input": {
                          cursor: formTravado ? "default" : "pointer",

                          color: "var(--ig-t-ffffff)",
                        },
                      }}
                      InputProps={{
                        readOnly: true,

                        endAdornment: (
                          <SearchIcon
                            sx={{
                              color: "var(--ig-t-ffffff)",

                              cursor: formTravado ? "default" : "pointer",
                            }}
                          />
                        ),
                      }}
                      placeholder="Clique para selecionar o imóvel"
                    />

                    <TextField
                      select
                      label="Complemento"
                      name="complemento"
                      value={formData.complemento}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("complemento")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                      slotProps={{
                        select: menuPropsDark,
                      }}
                    >
                      <MenuItem value="">Selecione o Complemento</MenuItem>

                      {locais.map((local) => (
                        <MenuItem key={local.id || local.nome} value={toUpperSafe(local.nome)}>
                          {toUpperSafe(local.nome)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>

                  <div className="form-row relogio-row-2">
                    <TextField
                      label="Nº Relógio / Hidrômetro"
                      name="numeroRelogio"
                      value={formData.numeroRelogio}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("numeroRelogio")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="Sub Nº Relógio"
                      name="subNumeroRelogio"
                      value={formData.subNumeroRelogio}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("subNumeroRelogio")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="Categoria Consumo"
                      name="categoriaConsumo"
                      value={formData.categoriaConsumo}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("categoriaConsumo")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />
                  </div>

                  <div className="form-row relogio-row-3">
                    <TextField
                      label="Nome Proprietário"
                      name="nomeProprietario"
                      value={formData.nomeProprietario}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("nomeProprietario")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="CPF/CNPJ Proprietário"
                      name="cpfProprietario"
                      value={formData.cpfProprietario}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("cpfProprietario")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="Proprietário igual ao Proprietário da Matrícula?"
                      value={proprietarioIgual}
                      disabled
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />
                  </div>

                  <div className="form-row relogio-row-3">
                    <TextField
                      label="Nome Titular / Consumidor"
                      name="nomeTitular"
                      value={formData.nomeTitular}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("nomeTitular")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="CPF/CNPJ Titular / Consumidor"
                      name="cpfTitular"
                      value={formData.cpfTitular}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("cpfTitular")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField label="Titular é igual ao Usuário / Locatário?" value={titularIgual} disabled fullWidth size="small" sx={muiCampoSx} />
                  </div>

                  <div className="form-row relogio-row-full">
                    <div className="relogio-busca-matricula">
                      <span className="relogio-label-matricula">Matrícula*</span>

                      {formTravado ? (
                        <TextField value={formData.matricula} disabled error={erroCampoMUI("matricula")} fullWidth size="small" sx={muiCampoSx} />
                      ) : (
                        <BuscarMatricula
                          onSelecionar={(mat) => {
                            const codigoMat = getCodigoMatricula(mat);

                            const apelidoMat = getApelidoMatricula(mat);

                            const texto = `${codigoMat} - ${apelidoMat}`.trim().toUpperCase();

                            setFormData((prev) => ({
                              ...prev,

                              matriculaId: mat?.id ?? prev.matriculaId ?? null,

                              matricula: texto,

                              matriculaCodigo: toUpperSafe(codigoMat),

                              matriculaApelido: toUpperSafe(apelidoMat),
                            }));

                            setNomeProprietarioMatricula(toUpperSafe(mat?.nome_proprietario ?? mat?.nomeProprietario ?? ""));

                            setNomeLocatario(toUpperSafe(mat?.nome_locatario ?? mat?.nomeLocatario ?? ""));
                          }}
                        />
                      )}
                    </div>
                  </div>

                  <div className="form-row relogio-row-full">
                    <TextField
                      label="Observação"
                      name="observacoes"
                      value={formData.observacoes}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("observacoes")}
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      sx={muiCampoSx}
                    />
                  </div>
                </div>
              )}

              {/* ABA SITUAÇÃO */}
              {mostrarSituacao && (
                <div className="conteudo-aba-formulario">
                  <div className="form-row relogio-row-situacao">
                    <TextField
                      select
                      label="Status"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("status")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                      slotProps={{
                        select: menuPropsDark,
                      }}
                    >
                      <MenuItem value="">Selecione Status</MenuItem>

                      <MenuItem value="ATIVO">Ativo</MenuItem>

                      <MenuItem value="INATIVO">Inativo</MenuItem>

                      <MenuItem value="FURTADO">Furtado</MenuItem>

                      <MenuItem value="CORTE">Corte</MenuItem>

                      <MenuItem value="SUSPENSO">Suspenso</MenuItem>
                    </TextField>

                    <TextField
                      select
                      label="Débito em Aberto?"
                      name="debitoAberto"
                      value={formData.debitoAberto}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("debitoAberto")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                      slotProps={{
                        select: menuPropsDark,
                      }}
                    >
                      <MenuItem value="NÃO">Não</MenuItem>

                      <MenuItem value="SIM">Sim</MenuItem>
                    </TextField>

                    {formData.debitoAberto === "SIM" && (
                      <TextField
                        label="Valor Débitos em Aberto"
                        name="valorDebito"
                        value={formData.valorDebito}
                        onChange={handleChange}
                        disabled={formTravado}
                        error={erroCampoMUI("valorDebito")}
                        fullWidth
                        size="small"
                        sx={muiCampoSx}
                      />
                    )}
                  </div>

                  <div className="form-row relogio-row-situacao">
                    <TextField
                      label="Mês da Última Competência"
                      type="month"
                      name="competencia"
                      value={formData.competencia}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("competencia")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                    />

                    <TextField
                      select
                      label="Cadastro Atualizado?"
                      name="atualizado"
                      value={formData.atualizado}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("atualizado")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                      slotProps={{
                        select: menuPropsDark,
                      }}
                    >
                      <MenuItem value="SIM">Sim</MenuItem>

                      <MenuItem value="NÃO">Não</MenuItem>
                    </TextField>
                  </div>
                </div>
              )}

              {/* AÇÕES */}
              <div className="form-botoes relogio-form-botoes">
                {modoVisualizacao && !editandoCodigo && podeEditar && (
                  <button
                    type="button"
                    className="btn-cadastrar"
                    onClick={() => {
                      setEditandoCodigo(idVisualizando);

                      setModoVisualizacao(false);
                    }}
                  >
                    <FaEdit />
                    Editar
                  </button>
                )}

                {(!modoVisualizacao || editandoCodigo) && (
                  <button type="submit" className="btn-cadastrar">
                    <FaSave />

                    {editandoCodigo ? "Atualizar" : "Cadastrar"}
                  </button>
                )}

                {(modoVisualizacao || editandoCodigo) && (
                  <button type="button" className="btn-acao" onClick={resetForm}>
                    <FaTimes />
                    Cancelar
                  </button>
                )}

                {podeMostrarExcluirNoForm && podeExcluir && (
                  <button type="button" className="btn-excluir" onClick={() => handleDelete(idVisualizando)}>
                    <FaTrash />
                    Excluir
                  </button>
                )}

                <button
                  type="button"
                  className="btn-ged btn-ged-form"
                  onClick={() => {
                    const input = document.getElementById("uploadGEDRelogioAgua");

                    input?.click();
                  }}
                  disabled={!podeCadastrar && !idVisualizando}
                >
                  <FaPaperclip />
                  GED
                  {(editandoCodigo || idVisualizando) &&
                    (() => {
                      const codigoAtual = editandoCodigo || idVisualizando;

                      const registroAtual = registros.find((registro) => registro.codigo === codigoAtual);

                      const quantidade = arquivos.length || Number(registroAtual?.qtdArquivos || 0);

                      return quantidade > 0 ? <span className="ged-badge">{quantidade}</span> : null;
                    })()}
                  {gedPendentes.length > 0 && !editandoCodigo && !idVisualizando && <span className="ged-badge">{gedPendentes.length}</span>}
                </button>
              </div>

              {/* GED DO FORMULÁRIO */}
              {(gedPendentes.length > 0 || ((editandoCodigo || idVisualizando) && arquivos.length > 0)) && (
                <div className="ged-formulario-container">
                  <div className="ged-formulario-cabecalho">
                    <div>
                      <h3>Arquivos GED</h3>

                      <span>
                        {gedPendentes.length > 0 && `${gedPendentes.length} arquivo(s) pendente(s)`}

                        {gedPendentes.length > 0 && arquivos.length > 0 && " • "}

                        {arquivos.length > 0 && `${arquivos.length} arquivo(s) anexado(s)`}
                      </span>
                    </div>

                    <FaPaperclip className="ged-formulario-icone" />
                  </div>

                  {!formTravado && gedPendentes.length > 0 && (
                    <div className="ged-formulario-secao">
                      <div className="ged-formulario-subtitulo">Arquivos pendentes para envio</div>

                      <div className="ged-formulario-lista">
                        {gedPendentes.map((arquivo, indice) => (
                          <div key={`${arquivo.name}-${indice}`} className="ged-formulario-item ged-formulario-item-pendente">
                            <div className="ged-formulario-arquivo">
                              <FaPaperclip />

                              <div className="ged-formulario-dados">
                                <span className="ged-formulario-nome" title={arquivo.name}>
                                  {arquivo.name}
                                </span>

                                <small>Aguardando o cadastro para envio</small>
                              </div>
                            </div>

                            <div className="ged-formulario-acoes">
                              <button
                                type="button"
                                className="ged-formulario-btn remover"
                                onClick={() => setGedPendentes((anteriores) => anteriores.filter((_, itemIndice) => itemIndice !== indice))}
                              >
                                <FaTimes />
                                Remover
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(editandoCodigo || idVisualizando) && arquivos.length > 0 && (
                    <div className="ged-formulario-secao">
                      <div className="ged-formulario-subtitulo">Arquivos anexados neste registro</div>

                      <div className="ged-formulario-lista">
                        {arquivos.map((arquivo) => {
                          const arquivoId = arquivo.id || arquivo.nome;

                          return (
                            <div key={arquivoId} className="ged-formulario-item">
                              <div className="ged-formulario-arquivo">
                                <FaPaperclip />

                                <div className="ged-formulario-dados">
                                  <span className="ged-formulario-nome" title={arquivo.nome}>
                                    {arquivo.nome}
                                  </span>

                                  <small>Arquivo anexado ao registro</small>
                                </div>
                              </div>

                              <div className="ged-formulario-acoes">
                                <button
                                  type="button"
                                  className="ged-formulario-btn visualizar"
                                  onClick={() => visualizarArquivoGED(editandoCodigo || idVisualizando, arquivoId, arquivo.nome)}
                                >
                                  <FaEye />
                                  Visualizar
                                </button>

                                <button
                                  type="button"
                                  className="ged-formulario-btn baixar"
                                  onClick={() => baixarArquivoGED(editandoCodigo || idVisualizando, arquivoId, arquivo.nome)}
                                >
                                  <i className="fas fa-download" />
                                  Baixar
                                </button>

                                {podeExcluir && (
                                  <button
                                    type="button"
                                    className="ged-formulario-btn excluir"
                                    onClick={() => excluirArquivoGED(editandoCodigo || idVisualizando, arquivoId)}
                                  >
                                    <FaTrash />
                                    Excluir
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <input
                type="file"
                id="uploadGEDRelogioAgua"
                multiple
                style={{
                  display: "none",
                }}
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);

                  if (!files.length) return;

                  e.target.value = "";

                  const codigoAtual = editandoCodigo || idVisualizando;

                  if (!codigoAtual) {
                    setGedPendentes((prev) => [...prev, ...files]);

                    alert(`Arquivos preparados (${files.length}). Clique em Cadastrar para anexar.`);

                    return;
                  }

                  const ok = await enviarGED(codigoAtual, files);

                  if (ok) {
                    alert("Arquivos enviados!");

                    await abrirGED(codigoAtual);

                    await carregarRegistros();
                  } else {
                    alert("Falha ao enviar arquivos.");
                  }
                }}
              />
            </form>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}

export default RelogioAgua;
